import axios from "axios";
import { Question, Responses, SuspectedHeadacheType } from "../types";
import { ClinicianDetails, PatientDetails } from "../types/user";
import { CalculatorCriteria } from "../types/calculator";

const apiClient = axios.create({
  baseURL: "https://api.meadache.ihealthe.org/api",
});

const getPatient = (patientId: string) =>
  apiClient.get(`/patients/v1/${patientId}`);
const updatePatient = (patientId: string, newPatientDetails: PatientDetails) =>
  apiClient.put(`/patients/v1/${patientId}`, newPatientDetails);

const getClinician = (clinicianId: string) =>
  apiClient.get(`/clinicians/v1/${clinicianId}`);
const updateClinician = (
  clinicianId: string,
  newClinicianDetails: ClinicianDetails,
) => apiClient.put(`/clinicians/v1/${clinicianId}`, newClinicianDetails);

const generateAccessCode = (clinicianId: string) =>
  apiClient.post(`/clinicians/v1/${clinicianId}/access-code`);

const getSharedPatients = (clinicianId: string) =>
  apiClient.get(`/clinicians/v1/${clinicianId}/patients`);
const unsharePatientClinician = (patientId: string, clinicianId: string) =>
  apiClient.delete(`/clinicians/v1/${clinicianId}/patients/${patientId}`);

const getHistoryQuestions = () => apiClient.get("/forms/v1/history");
const getRedFlags = () => apiClient.get("/forms/v1/red-flags");
const getQuestions = () => apiClient.get("/forms/v1/questions");
const editQuestion = (
  question: Question
) =>
  apiClient.put(`/forms/v1/questions/` + question.questionId, {
    question: question,
  });
const addQuestion = (question: Question) => apiClient.post("/forms/v1/questions", {
  question: question,
});
const getCalculators = () => apiClient.get("/forms/v1/calculators");
const getCalculator = (calculatorId: string) =>
  apiClient.get(`/forms/v1/calculators/${calculatorId}`);
const getCalculatorCriteria = (calculatorId: string) =>
  apiClient.get(`/forms/v1/calculators/${calculatorId}/criteria`);
const addCalculator = (calculator: CalculatorCriteria) =>
  apiClient.post("/forms/v1/calculators", {
    calculator: calculator,
  });
const editCalculator = (calculator: CalculatorCriteria) => apiClient.put(`/forms/v1/calculators/${calculator.calculatorId}/criteria`, {
  calculator: calculator,
});
const useCalculator = (calculatorId: string, responses: Responses) =>
  apiClient.post(`/forms/v1/calculators/${calculatorId}`, {
    questions: responses,
  });

const getPatientHistories = (patientId: string) =>
  apiClient.get(`/patients/v1/${patientId}/histories`);
const addPatientHistory = (patientId: string, responses: Responses) =>
  apiClient.post(`/patients/v1/${patientId}/histories`, {
    questions: responses,
  });
const editPatientHistory = (
  patientId: string,
  historyId: number,
  responses: Responses,
) =>
  apiClient.put(`/patients/v1/${patientId}/histories/${historyId}`, {
    questions: responses,
  });
const deletePatientHistory = (patientId: string, historyId: number) =>
  apiClient.delete(`/patients/v1/${patientId}/histories/${historyId}`);

const downloadHistoryPdf = (patientId: string, historyId: number) =>
  apiClient.get(`/patients/v1/${patientId}/histories/${historyId}/pdf`);

const getAttacks = (patientId: string) =>
  apiClient.get(`/patients/v1/${patientId}/diary/attacks`);

const addAttack = (
  patientId: string,
  newAttack: {
    startDateTime: string;
    endDateTime: string;
    awake: boolean;
    disabledForTwoHours: boolean;
    suspectedHeadacheType: SuspectedHeadacheType;
    notes: string;
  },
) => apiClient.post(`/patients/v1/${patientId}/diary/attacks`, newAttack);

const editAttack = (
  patientId: string,
  attackId: number,
  newAttack: {
    startDateTime: string;
    endDateTime: string;
    awake: boolean;
    disabledForTwoHours: boolean;
    suspectedHeadacheType: SuspectedHeadacheType;
    notes: string;
  },
) =>
  apiClient.put(
    `/patients/v1/${patientId}/diary/attacks/${attackId}`,
    newAttack,
  );

const deleteAttack = (patientId: string, attackId: number) =>
  apiClient.delete(`/patients/v1/${patientId}/diary/attacks/${attackId}`);

const getMenstruations = (patientId: string) =>
  apiClient.get(`/patients/v1/${patientId}/diary/menstruations`);

const addMenstruation = (
  patientId: string,
  newMenstruation: { startDate: string; endDate: string },
) =>
  apiClient.post(
    `/patients/v1/${patientId}/diary/menstruations`,
    newMenstruation,
  );

const editMenstruation = (
  patientId: string,
  menstruationId: number,
  newMenstruation: { startDate: string; endDate: string },
) =>
  apiClient.put(
    `/patients/v1/${patientId}/diary/menstruations/${menstruationId}`,
    newMenstruation,
  );

const deleteMenstruation = (patientId: string, menstruationId: number) =>
  apiClient.delete(
    `/patients/v1/${patientId}/diary/menstruations/${menstruationId}`,
  );

const downloadDiaryPdf = (patientId: string) =>
  apiClient.get(`/patients/v1/${patientId}/diary/pdf`, {
    responseType: "arraybuffer",
  });

export {
  getPatient,
  updatePatient,
  getClinician,
  updateClinician,
  generateAccessCode,
  getSharedPatients,
  unsharePatientClinician,
  getHistoryQuestions,
  getRedFlags,
  getQuestions,
  editQuestion,
  addQuestion,
  getCalculators,
  getCalculator,
  getCalculatorCriteria,
  addCalculator,
  editCalculator,
  useCalculator,
  getPatientHistories,
  addPatientHistory,
  editPatientHistory,
  deletePatientHistory,
  downloadHistoryPdf,
  getAttacks,
  addAttack,
  editAttack,
  deleteAttack,
  getMenstruations,
  addMenstruation,
  editMenstruation,
  deleteMenstruation,
  downloadDiaryPdf,
};

